import { useEffect, useState, useCallback } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Save, Plus, Trash2, ShieldCheck, Search, FileText, Store, CreditCard, ToggleLeft, Mail, RotateCcw, Send, Link2, Eye, EyeOff, Facebook, Instagram, Youtube, Linkedin, MessageCircle, Music2, MapPin, Globe, X as XIcon, CalendarDays, Phone, AlertTriangle, Upload, ImageIcon } from 'lucide-react';
import { Twitch } from 'lucide-react';
import { Star } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { SettingsEmailTab } from './settings/SettingsEmailTab';
import { SettingsSeoTab } from './settings/SettingsSeoTab';
import { SettingsContentTab } from './settings/SettingsContentTab';

const SOCIAL_ICON_LIST = [
  { key: 'instagram', icon: Instagram, label: 'Instagram', bg: 'bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737]', ring: 'ring-[#E1306C]/40' },
  { key: 'facebook', icon: Facebook, label: 'Facebook', bg: 'bg-[#1877F2]', ring: 'ring-[#1877F2]/40' },
  { key: 'twitter', icon: XIcon, label: 'X (Twitter)', bg: 'bg-black', ring: 'ring-black/30' },
  { key: 'youtube', icon: Youtube, label: 'YouTube', bg: 'bg-[#FF0000]', ring: 'ring-[#FF0000]/40' },
  { key: 'tiktok', icon: Music2, label: 'TikTok', bg: 'bg-black', ring: 'ring-black/30' },
  { key: 'linkedin', icon: Linkedin, label: 'LinkedIn', bg: 'bg-[#0A66C2]', ring: 'ring-[#0A66C2]/40' },
  { key: 'pinterest', icon: MapPin, label: 'Pinterest', bg: 'bg-[#E60023]', ring: 'ring-[#E60023]/40' },
  { key: 'threads', icon: MessageCircle, label: 'Threads', bg: 'bg-black', ring: 'ring-black/30' },
  { key: 'whatsapp', icon: MessageCircle, label: 'WhatsApp', bg: 'bg-[#25D366]', ring: 'ring-[#25D366]/40' },
  { key: 'telegram', icon: Send, label: 'Telegram', bg: 'bg-[#0088CC]', ring: 'ring-[#0088CC]/40' },
  { key: 'snapchat', icon: Globe, label: 'Snapchat', bg: 'bg-[#FFFC00]', ring: 'ring-[#FFFC00]/40' },
  { key: 'twitch', icon: Twitch, label: 'Twitch', bg: 'bg-[#9146FF]', ring: 'ring-[#9146FF]/40' },
  { key: 'discord', icon: MessageCircle, label: 'Discord', bg: 'bg-[#5865F2]', ring: 'ring-[#5865F2]/40' },
  { key: 'tripadvisor', icon: MapPin, label: 'TripAdvisor', bg: 'bg-[#00AF87]', ring: 'ring-[#00AF87]/40' },
  { key: 'yelp', icon: Star, label: 'Yelp', bg: 'bg-[#D32323]', ring: 'ring-[#D32323]/40' },
  { key: 'website', icon: Globe, label: 'Site web', bg: 'bg-garden-700', ring: 'ring-garden-700/40' },
];

function FeaturedFormulesSelector({ settings, update }) {
  const [formules, setFormules] = useState([]);
  useEffect(() => {
    adminApi.getFormules().then(r => setFormules(r.data || [])).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = settings.featured_formules || [];
  const toggle = (id) => {
    const newSel = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
    update('featured_formules', newSel);
  };

  return (
    <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-4">
      <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Formules mises en avant (accueil)</h3>
      <p className="text-xs text-charcoal/50">Sélectionnez les formules à afficher sur la page d'accueil. Si aucune n'est sélectionnée, les 2 premières sont affichées par défaut.</p>
      {formules.length === 0 ? (
        <p className="text-sm text-charcoal/50">Aucune formule trouvée. Créez-en dans le menu "Carte".</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {formules.map(f => (
            <button
              key={f.id}
              onClick={() => toggle(f.id)}
              className={`text-left p-3 rounded-xl border-2 transition-all ${selected.includes(f.id) ? 'border-garden-600 bg-garden-50' : 'border-garden-200 bg-white hover:border-garden-400'}`}
              data-testid={`featured-formule-${f.id}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm text-garden-800">{f.name}</span>
                <span className="text-sm font-bold text-garden-600">{f.price}€</span>
              </div>
              {f.description && <p className="text-xs text-charcoal/50 mt-1">{f.description}</p>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Settings() {
  const { user, hasPermission } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const canEditContent = hasPermission('SETTINGS_CONTENT', 'edit');
  const canViewPayment = hasPermission('SETTINGS_PAYMENT', 'view');
  const canEditPayment = hasPermission('SETTINGS_PAYMENT', 'edit');
  const canViewSecurity = hasPermission('SETTINGS_SECURITY', 'view');
  const canEditSecurity = hasPermission('SETTINGS_SECURITY', 'edit');
  const [settings, setSettings] = useState({});
  const [modules, setModules] = useState({});
  const [emailStatus, setEmailStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newTag, setNewTag] = useState('');
  const [newSocial, setNewSocial] = useState({ name: '', url: '', icon_key: '' });
  const [showStripeKey, setShowStripeKey] = useState(false);
  const [generatingKeywords, setGeneratingKeywords] = useState(false);
  const [keywordDescription, setKeywordDescription] = useState('');
  const [suggestedKeywords, setSuggestedKeywords] = useState([]);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  const loadEmailStatus = useCallback(() => {
    adminApi.getEmailStatus().then(r => setEmailStatus(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    adminApi.getSettings().then(r => setSettings(r.data || {})).catch(() => {});
    loadEmailStatus();
    if (isAdmin) {
      adminApi.getModules().then(r => setModules(r.data || {})).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, loadEmailStatus]);

  const update = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

  const addEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) { toast.error('Email invalide'); return; }
    const current = settings.allowed_emails || [];
    if (current.includes(email)) { toast.error('Email déjà ajouté'); return; }
    update('allowed_emails', [...current, email]);
    setNewEmail('');
    toast.success(`${email} ajouté`);
  };

  const removeEmail = (email) => {
    update('allowed_emails', (settings.allowed_emails || []).filter(e => e !== email));
  };

  const addTag = () => {
    const tag = newTag.trim();
    if (!tag) return;
    const current = settings.about_tags || [];
    if (current.includes(tag)) { toast.error('Tag déjà ajouté'); return; }
    update('about_tags', [...current, tag]);
    setNewTag('');
  };

  const removeTag = (tag) => {
    update('about_tags', (settings.about_tags || []).filter(t => t !== tag));
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.updateSettings(settings);
      toast.success('Paramètres sauvegardés');
      loadEmailStatus();
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const saveModules = async () => {
    setSaving(true);
    try {
      await adminApi.updateModules(modules);
      toast.success('Modules mis à jour');
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  return (
    <AdminLayout title="Paramètres">
      <div data-testid="admin-settings" className="max-w-4xl">
        <Tabs defaultValue="general">
          <TabsList className="bg-garden-100 mb-6 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="general" className="data-[state=active]:bg-garden-800 data-[state=active]:text-white gap-1.5">
              <Store size={14} /> Infos
            </TabsTrigger>
            <TabsTrigger value="content" className="data-[state=active]:bg-garden-800 data-[state=active]:text-white gap-1.5">
              <FileText size={14} /> Contenu
            </TabsTrigger>
            <TabsTrigger value="seo" className="data-[state=active]:bg-garden-800 data-[state=active]:text-white gap-1.5">
              <Search size={14} /> SEO
            </TabsTrigger>
            <TabsTrigger value="email" className="data-[state=active]:bg-garden-800 data-[state=active]:text-white gap-1.5">
              <Mail size={14} /> Email
            </TabsTrigger>
            <TabsTrigger value="payment" className="data-[state=active]:bg-garden-800 data-[state=active]:text-white gap-1.5" disabled={!canViewPayment}>
              <CreditCard size={14} /> Paiement
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="modules" className="data-[state=active]:bg-garden-800 data-[state=active]:text-white gap-1.5">
                <ToggleLeft size={14} /> Modules
              </TabsTrigger>
            )}
            {canViewSecurity && (
              <TabsTrigger value="security" className="data-[state=active]:bg-garden-800 data-[state=active]:text-white gap-1.5">
                <ShieldCheck size={14} /> Sécurité
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="system" className="data-[state=active]:bg-garden-800 data-[state=active]:text-white gap-1.5">
                <RotateCcw size={14} /> Système
              </TabsTrigger>
            )}
            <TabsTrigger value="legal" className="data-[state=active]:bg-garden-800 data-[state=active]:text-white">Légal</TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="maintenance" className="data-[state=active]:bg-garden-800 data-[state=active]:text-white gap-1.5">
                <ToggleLeft size={14} /> Maintenance
              </TabsTrigger>
            )}
            <TabsTrigger value="reservation" className="data-[state=active]:bg-garden-800 data-[state=active]:text-white gap-1.5">
              <CalendarDays size={14} /> Réservation
            </TabsTrigger>
          </TabsList>

          {/* INFOS GÉNÉRALES */}
          <TabsContent value="general">
            <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
              <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Informations du restaurant</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Nom du restaurant</Label>
                  <Input value={settings.name || ''} onChange={e => update('name', e.target.value)} className="border-garden-200 rounded-xl" data-testid="settings-name" />
                </div>
                <div>
                  <Label className="text-sm">Slogan</Label>
                  <Input value={settings.slogan || ''} onChange={e => update('slogan', e.target.value)} className="border-garden-200 rounded-xl" placeholder="Ex: Restaurant convivial à Bordeaux" />
                </div>
              </div>
              <div>
                <Label className="text-sm">Adresse complète</Label>
                <Input value={settings.address || ''} onChange={e => update('address', e.target.value)} className="border-garden-200 rounded-xl" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Téléphone</Label>
                  <Input value={settings.phone || ''} onChange={e => update('phone', e.target.value)} className="border-garden-200 rounded-xl" />
                </div>
                <div>
                  <Label className="text-sm">Email de contact</Label>
                  <Input value={settings.email || ''} onChange={e => update('email', e.target.value)} className="border-garden-200 rounded-xl" />
                </div>
              </div>
              <div>
                <Label className="text-sm">Lien Google Maps (pour le bouton "Itinéraire")</Label>
                <Input value={settings.maps_url || ''} onChange={e => update('maps_url', e.target.value)} className="border-garden-200 rounded-xl" placeholder="https://maps.google.com/..." />
              </div>
              <div>
                <Label className="text-sm">Moyens de paiement acceptés (séparés par des virgules)</Label>
                <Input value={(settings.payment_methods || []).join(', ')} onChange={e => update('payment_methods', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} className="border-garden-200 rounded-xl" />
              </div>
              <div>
                <Label className="text-sm">Comment venir (transports, parking)</Label>
                <Textarea value={settings.access_info || ''} onChange={e => update('access_info', e.target.value)} className="border-garden-200 rounded-xl" rows={2} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Page Facebook</Label>
                  <Input value={settings.social_facebook || ''} onChange={e => update('social_facebook', e.target.value)} className="border-garden-200 rounded-xl" placeholder="https://facebook.com/..." data-testid="settings-facebook-url" />
                </div>
                <div>
                  <Label className="text-sm">Compte Instagram</Label>
                  <Input value={settings.social_instagram || ''} onChange={e => update('social_instagram', e.target.value)} className="border-garden-200 rounded-xl" placeholder="https://instagram.com/..." data-testid="settings-instagram-url" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Pinterest</Label>
                  <Input value={settings.social_pinterest || ''} onChange={e => update('social_pinterest', e.target.value)} className="border-garden-200 rounded-xl" placeholder="https://pinterest.com/..." data-testid="settings-pinterest-url" />
                </div>
                <div>
                  <Label className="text-sm">X (Twitter)</Label>
                  <Input value={settings.social_twitter || ''} onChange={e => update('social_twitter', e.target.value)} className="border-garden-200 rounded-xl" placeholder="https://x.com/..." data-testid="settings-twitter-url" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Threads</Label>
                  <Input value={settings.social_threads || ''} onChange={e => update('social_threads', e.target.value)} className="border-garden-200 rounded-xl" placeholder="https://threads.net/..." data-testid="settings-threads-url" />
                </div>
              </div>

              {/* Custom social networks */}
              <div className="border-t border-garden-100 pt-4">
                <h4 className="font-medium text-garden-800 text-sm mb-3">Réseaux personnalisés</h4>
                {(settings.custom_socials || []).map((s, i) => {
                  const iconInfo = SOCIAL_ICON_LIST.find(x => x.key === s.icon_key);
                  const IconComp = iconInfo?.icon;
                  return (
                    <div key={`social-${s.icon_key}-${s.url}`} className="flex items-center gap-3 mb-2 bg-garden-50 rounded-lg p-2.5">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${iconInfo?.bg || 'bg-garden-700'}`}>
                        {IconComp ? <IconComp size={14} className="text-white" /> : <span className="text-white text-xs">{s.icon_key?.charAt(0) || '?'}</span>}
                      </div>
                      <span className="text-sm font-medium text-garden-800 flex-1">{s.name}</span>
                      <span className="text-xs text-charcoal/50 truncate max-w-[200px]">{s.url}</span>
                      <button onClick={() => update('custom_socials', (settings.custom_socials || []).filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                    </div>
                  );
                })}
                
                {/* Add new custom network */}
                <div className="mt-3 border border-garden-200 rounded-xl p-3 bg-white space-y-3">
                  <Label className="text-xs font-medium text-garden-700">Ajouter un réseau</Label>
                  
                  {/* Icon picker grid */}
                  <div>
                    <Label className="text-xs text-charcoal/50 mb-1.5 block">Choisir une icône</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {SOCIAL_ICON_LIST.map(si => {
                        const SiIcon = si.icon;
                        const selected = newSocial.icon_key === si.key;
                        return (
                          <button
                            key={si.key}
                            type="button"
                            onClick={() => setNewSocial({...newSocial, icon_key: si.key, name: newSocial.name || si.label})}
                            className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${selected ? `${si.bg} ring-2 ring-offset-1 ${si.ring} scale-110` : 'bg-gray-100 hover:bg-gray-200'}`}
                            title={si.label}
                            data-testid={`icon-pick-${si.key}`}
                          >
                            <SiIcon size={14} className={selected ? 'text-white' : 'text-charcoal/60'} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 items-end">
                    <div className="flex-1 min-w-[120px]">
                      <Label className="text-xs">Nom</Label>
                      <Input value={newSocial.name} onChange={e => setNewSocial({...newSocial, name: e.target.value})} className="border-garden-200 rounded-lg h-9" placeholder="TikTok" data-testid="custom-social-name" />
                    </div>
                    <div className="flex-[2] min-w-[180px]">
                      <Label className="text-xs">Lien</Label>
                      <Input value={newSocial.url} onChange={e => setNewSocial({...newSocial, url: e.target.value})} className="border-garden-200 rounded-lg h-9" placeholder="https://..." data-testid="custom-social-url" />
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        if (!newSocial.name || !newSocial.url) { toast.error('Nom et lien requis'); return; }
                        if (!newSocial.icon_key) { toast.error('Choisissez une icône'); return; }
                        update('custom_socials', [...(settings.custom_socials || []), { name: newSocial.name, url: newSocial.url, icon_key: newSocial.icon_key }]);
                        setNewSocial({ name: '', url: '', icon_key: '' });
                      }}
                      className="bg-garden-800 hover:bg-garden-900 text-white rounded-lg h-9"
                      data-testid="add-custom-social-btn"
                    >
                      <Plus size={14} className="mr-1" /> Ajouter
                    </Button>
                  </div>
                </div>
              </div>

              <div className="bg-garden-50 rounded-xl p-4 space-y-4">
                <h4 className="font-medium text-garden-800 text-sm">Affichage sur le site</h4>
                <div>
                  <Label className="text-sm">Texte d'accroche réseaux sociaux</Label>
                  <Textarea 
                    value={settings.social_text || ''} 
                    onChange={e => update('social_text', e.target.value)} 
                    className="border-garden-200 rounded-xl bg-white" 
                    rows={2}
                    placeholder="Suivez Côté Terrasse sur les réseaux sociaux..."
                    data-testid="settings-social-text"
                  />
                </div>
                <div className="flex flex-wrap gap-4">
                  {[
                    ['show_facebook', 'Facebook'],
                    ['show_instagram', 'Instagram'],
                    ['show_pinterest', 'Pinterest'],
                    ['show_twitter', 'X (Twitter)'],
                    ['show_threads', 'Threads'],
                  ].map(([key, label]) => (
                    <div key={key} className="flex items-center gap-2">
                      <Switch checked={settings[key] !== false} onCheckedChange={v => update(key, v)} data-testid={`settings-${key}`} />
                      <Label className="text-sm">{label}</Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* CONTENU ÉDITABLE */}
          <TabsContent value="content">
            <SettingsContentTab settings={settings} update={update} />
            <FeaturedFormulesSelector settings={settings} update={update} />
          </TabsContent>

          {/* SEO */}
          <TabsContent value="seo">
            <SettingsSeoTab settings={settings} update={update} />
          </TabsContent>

          {/* EMAIL */}
          <TabsContent value="email">
            <SettingsEmailTab settings={settings} update={update} emailStatus={emailStatus} loadEmailStatus={loadEmailStatus} save={save} />
          </TabsContent>

          {/* PAIEMENT */}
          {canViewPayment && (
          <TabsContent value="payment">
            <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
              <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Paiement en ligne (Stripe)</h3>
              
              {settings.stripe_publishable_key && settings.stripe_secret_key ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3" data-testid="stripe-status-active">
                  <CreditCard className="text-green-600 mt-0.5 shrink-0" size={18} />
                  <div>
                    <p className="text-sm font-medium text-green-800">Stripe configuré</p>
                    <p className="text-xs text-green-700 mt-1">Les clés Stripe sont renseignées. Activez le module Paiement dans l'onglet "Modules" pour accepter les paiements.</p>
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-start gap-3" data-testid="stripe-status-inactive">
                  <CreditCard className="text-amber-600 mt-0.5 shrink-0" size={18} />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Stripe non configuré</p>
                    <p className="text-xs text-amber-700 mt-1">Renseignez vos clés Stripe ci-dessous pour activer le paiement en ligne.</p>
                  </div>
                </div>
              )}

              {canEditPayment ? (
              <div className="space-y-4">
                <div>
                  <Label className="text-sm">Clé publique Stripe (Publishable key)</Label>
                  <Input value={settings.stripe_publishable_key || ''} onChange={e => update('stripe_publishable_key', e.target.value)} className="border-garden-200 rounded-xl font-mono text-sm" placeholder="pk_test_..." data-testid="stripe-publishable-key" />
                </div>
                <div>
                  <Label className="text-sm">Clé secrète Stripe (Secret key)</Label>
                  <div className="relative">
                    <Input type={showStripeKey ? 'text' : 'password'} value={settings.stripe_secret_key || ''} onChange={e => update('stripe_secret_key', e.target.value)} className="border-garden-200 rounded-xl font-mono text-sm pr-10" placeholder="sk_test_..." data-testid="stripe-secret-key" />
                    <button type="button" onClick={() => setShowStripeKey(!showStripeKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal/40 hover:text-charcoal/70">
                      {showStripeKey ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <p className="text-xs text-red-500/70 mt-1">Ne partagez jamais cette clé.</p>
                </div>
              </div>
              ) : (
                <p className="text-sm text-charcoal/50">Vous n'avez pas la permission de modifier les clés de paiement.</p>
              )}
            </div>
          </TabsContent>
          )}

          {/* MODULES (Admin only) */}
          {isAdmin && (
            <TabsContent value="modules">
              <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
                <div className="flex items-start gap-3">
                  <ToggleLeft size={20} className="text-garden-600 mt-0.5 shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-garden-800">Mode vitrine et modules</h3>
                    <p className="text-xs text-charcoal/60 mt-1">
                      Activez ou désactivez les fonctionnalités avancées du site. En mode vitrine, seul le contenu est affiché.
                    </p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border border-garden-200 rounded-xl">
                    <div>
                      <h4 className="font-medium text-charcoal">Réservations</h4>
                      <p className="text-xs text-charcoal/60">Gestion des réservations dans le back-office. Sinon, envoi par email uniquement.</p>
                    </div>
                    <Switch 
                      checked={modules.reservationsModuleEnabled || false}
                      onCheckedChange={v => setModules({ ...modules, reservationsModuleEnabled: v })}
                      data-testid="module-reservations"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 border border-garden-200 rounded-xl">
                    <div>
                      <h4 className="font-medium text-charcoal">Commandes en ligne</h4>
                      <p className="text-xs text-charcoal/60">Click & Collect avec gestion des commandes. Inclut le paiement en ligne.</p>
                    </div>
                    <Switch 
                      checked={modules.ordersModuleEnabled || false}
                      onCheckedChange={v => setModules({ ...modules, ordersModuleEnabled: v })}
                      data-testid="module-orders"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 border border-garden-200 rounded-xl">
                    <div>
                      <h4 className="font-medium text-charcoal">OCR Menu</h4>
                      <p className="text-xs text-charcoal/60">Reconnaissance de menus écrits à la main par photo.</p>
                    </div>
                    <Switch 
                      checked={modules.ocrMenuEnabled || false}
                      onCheckedChange={v => setModules({ ...modules, ocrMenuEnabled: v })}
                      data-testid="module-ocr"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 border border-garden-200 rounded-xl">
                    <div>
                      <h4 className="font-medium text-charcoal">Paiement en ligne</h4>
                      <p className="text-xs text-charcoal/60">Paiement via Stripe pour les commandes en ligne.</p>
                    </div>
                    <Switch 
                      checked={modules.paymentsModuleEnabled || false}
                      onCheckedChange={v => setModules({ ...modules, paymentsModuleEnabled: v })}
                      data-testid="module-payments"
                    />
                  </div>
                </div>
                
                <Button onClick={saveModules} disabled={saving} className="bg-garden-800 hover:bg-garden-900 text-white rounded-full">
                  <Save size={16} className="mr-1" /> {saving ? 'Sauvegarde...' : 'Sauvegarder les modules'}
                </Button>
              </div>
            </TabsContent>
          )}

          {/* SÉCURITÉ */}
          {canViewSecurity && (
            <TabsContent value="security">
              <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
                <div className="flex items-start gap-3">
                  <ShieldCheck size={20} className="text-garden-600 mt-0.5 shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-garden-800">Paramètres de sécurité</h3>
                    <p className="text-xs text-charcoal/60 mt-1">Configuration des clés et accès sensibles de l'application.</p>
                  </div>
                </div>
                
                {canEditSecurity ? (
                  <div className="space-y-5">
                    <div>
                      <Label className="text-sm">Google Client ID</Label>
                      <Input value={settings.google_client_id || ''} onChange={e => update('google_client_id', e.target.value)} className="border-garden-200 rounded-xl font-mono text-sm" placeholder="xxxx.apps.googleusercontent.com" />
                      <p className="text-xs text-charcoal/50 mt-1">Identifiant OAuth 2.0 de votre projet Google Cloud. Obtenez-le dans la <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-garden-600 underline">Console Google Cloud</a> &gt; Identifiants &gt; ID client OAuth 2.0.</p>
                    </div>
                    <div>
                      <Label className="text-sm">Google Client Secret</Label>
                      <Input type="password" value={settings.google_client_secret || ''} onChange={e => update('google_client_secret', e.target.value)} className="border-garden-200 rounded-xl font-mono text-sm" />
                      <p className="text-xs text-charcoal/50 mt-1">Clé secrète associée au Client ID ci-dessus. Ne partagez jamais cette valeur. Elle est stockée de maniere chiffree en base de donnees.</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-charcoal/50">Vous n'avez pas la permission de modifier les paramètres de sécurité.</p>
                )}
              </div>
            </TabsContent>
          )}

          {/* MENTIONS LÉGALES */}
          <TabsContent value="legal">
            <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
              <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Mentions légales</h3>
              <div>
                <Label className="text-sm">Éditeur du site</Label>
                <Input value={settings.legal_editor || ''} onChange={e => update('legal_editor', e.target.value)} className="border-garden-200 rounded-xl" placeholder="Côté Terrasse SARL" />
              </div>
              <div>
                <Label className="text-sm">Hébergeur</Label>
                <Input value={settings.legal_host || ''} onChange={e => update('legal_host', e.target.value)} className="border-garden-200 rounded-xl" />
              </div>
              <div>
                <Label className="text-sm">Responsable de publication</Label>
                <Input value={settings.legal_responsible || ''} onChange={e => update('legal_responsible', e.target.value)} className="border-garden-200 rounded-xl" />
              </div>
            </div>
          </TabsContent>

          {/* SYSTÈME */}
          {isAdmin && (
            <TabsContent value="system">
              <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
                <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Données de démonstration</h3>
                <p className="text-sm text-charcoal/70">
                  Réinitialisez les données de démonstration (menu, horaires, photos, avis) si elles ont été supprimées ou corrompues. 
                  Cette action ne supprime pas les données existantes, elle ajoute uniquement les éléments manquants.
                </p>
                <Button
                  onClick={async () => {
                    setResetting(true);
                    try {
                      await adminApi.resetDemoData();
                      toast.success('Données de démonstration restaurées !');
                    } catch { toast.error('Erreur lors de la réinitialisation'); }
                    setResetting(false);
                  }}
                  disabled={resetting}
                  data-testid="reset-demo-data-btn"
                  className="bg-amber-600 hover:bg-amber-700 text-white rounded-full"
                >
                  <RotateCcw size={16} className={`mr-1 ${resetting ? 'animate-spin' : ''}`} />
                  {resetting ? 'Réinitialisation...' : 'Réinitialiser les données de démo'}
                </Button>
              </div>
            </TabsContent>
          )}

          {/* MAINTENANCE */}
          {isAdmin && (
            <TabsContent value="maintenance">
              <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-garden-800">Mode maintenance</h3>
                    <p className="text-xs text-charcoal/60 mt-1">
                      Quand activé, le site public affiche une page de présentation avec les coordonnées du restaurant. L'admin reste accessible.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <Switch
                    checked={settings.maintenance_mode === true || settings.maintenance_mode === 'true'}
                    onCheckedChange={(v) => update('maintenance_mode', v)}
                    data-testid="maintenance-toggle"
                  />
                  <div>
                    <p className="text-sm font-medium text-amber-800">
                      {settings.maintenance_mode === true || settings.maintenance_mode === 'true' ? 'Mode maintenance ACTIVÉ' : 'Mode maintenance désactivé'}
                    </p>
                    <p className="text-xs text-amber-700">Le site public est {settings.maintenance_mode === true || settings.maintenance_mode === 'true' ? 'masqué' : 'visible'}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Message de maintenance</Label>
                  <Textarea
                    value={settings.maintenance_message || ''}
                    onChange={e => update('maintenance_message', e.target.value)}
                    className="border-garden-200 rounded-xl"
                    rows={3}
                    placeholder="Notre site est en cours de mise à jour. Nous revenons très bientôt !"
                    data-testid="maintenance-message"
                  />
                  <p className="text-xs text-charcoal/50 mt-1">Ce message s'affiche sur la page de maintenance. Les coordonnées, horaires et réseaux sociaux sont repris automatiquement.</p>
                </div>
              </div>
            </TabsContent>
          )}

          {/* RÉSERVATION */}
          <TabsContent value="reservation">
            <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
              <div className="flex items-start gap-3">
                <CalendarDays size={20} className="text-garden-600 mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-heading font-semibold text-garden-800">Mode de réservation</h3>
                  <p className="text-xs text-charcoal/60 mt-1">
                    Choisissez comment vos clients réservent une table. Le site s'adapte automatiquement.
                  </p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <button
                  onClick={() => update('reservation_mode', 'online')}
                  className={`p-5 rounded-xl border-2 text-left transition-all ${
                    (!settings.reservation_mode || settings.reservation_mode === 'online')
                      ? 'border-garden-600 bg-garden-50'
                      : 'border-garden-200 hover:border-garden-300'
                  }`}
                  data-testid="reservation-mode-online"
                >
                  <CalendarDays size={24} className="text-garden-700 mb-2" />
                  <p className="font-medium text-garden-800">Réservation en ligne</p>
                  <p className="text-xs text-charcoal/60 mt-1">Les clients remplissent un formulaire de réservation directement sur le site.</p>
                </button>
                <button
                  onClick={() => update('reservation_mode', 'phone')}
                  className={`p-5 rounded-xl border-2 text-left transition-all ${
                    settings.reservation_mode === 'phone'
                      ? 'border-garden-600 bg-garden-50'
                      : 'border-garden-200 hover:border-garden-300'
                  }`}
                  data-testid="reservation-mode-phone"
                >
                  <Phone size={24} className="text-garden-700 mb-2" />
                  <p className="font-medium text-garden-800">Réservation par téléphone</p>
                  <p className="text-xs text-charcoal/60 mt-1">La page affiche votre numéro de téléphone avec un bouton d'appel.</p>
                </button>
              </div>
              {settings.reservation_mode === 'phone' && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-sm text-blue-800">
                    Assurez-vous que votre numéro de téléphone est renseigné dans l'onglet <strong>Infos</strong>. C'est ce numéro qui sera affiché sur la page de réservation.
                  </p>
                  {settings.phone && (
                    <p className="text-sm font-medium text-blue-900 mt-2">Numéro actuel : {settings.phone}</p>
                  )}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <Button onClick={save} disabled={saving || !canEditContent} data-testid="admin-save-settings-btn" className="bg-garden-800 hover:bg-garden-900 text-white rounded-full mt-6 disabled:opacity-50">
          <Save size={16} className="mr-1" /> {saving ? 'Sauvegarde...' : canEditContent ? 'Sauvegarder les paramètres' : 'Lecture seule'}
        </Button>
      </div>
    </AdminLayout>
  );
}
